import React, { useState } from 'react';

import { useSelector } from 'react-redux';

import IconButton from '@material-ui/core/IconButton';

import { userSelector } from '../../../reducers/current-user-selectors';
import { jobsForOrgIdSelector } from '../../../reducers/job-selectors';

import { ApplicantMessageTemplateParser } from '../../../message-templates/parsers/applicant-message-template-parser';
import { UiButton } from '../../../ui/ui-button';

import { ApplicantAutomationDialog } from './applicant-automation-dialog';
import { EmployeeAutomationDialog } from './employee-automation-dialog';

import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import { CreatableApplicantAutomationTypes } from '../../utils/applicant-automation-communication-types';
import {
  AutomationsByTriggerDictionary,
  getApplicantAutomationCreateDefaults,
} from '../../utils/applicant-automation-display-utils';
import { isEmployeeAutomationTrigger } from '../../utils/employee-automation-display-utils';

export const AddAutomationButton = ({
  variant,
  triggerType,
  section,
  org,
  editingForJob,
  onClick,
  className,
  parentAutomationsForSection,
  interval,
}: {
  variant: 'small' | 'medium' | 'large';
  triggerType:
    | 'applicant'
    | CreatableApplicantAutomationTypes
    | StartDateTriggeredCommunicationTriggerType;
  section?: ApplicantAutomationSection;
  org: Org;
  editingForJob?: Job;
  onClick?: () => void;
  className?: string;
  parentAutomationsForSection?: AutomationsByTriggerDictionary;
  interval?: EmployeeAutomationSendInterval;
}) => {
  const user = useSelector((state: any) => userSelector(state));
  const jobs = useSelector((state: any) => jobsForOrgIdSelector(state, org.org_id));

  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const toggleDialog = () => setDialogVisible(!dialogVisible);

  const handleAddAutomation = () => {
    if (onClick) {
      onClick();
    } else {
      toggleDialog();
    }
    return null;
  };

  const renderDialog = () => {
    if (isEmployeeAutomationTrigger(triggerType)) {
      return (
        <EmployeeAutomationDialog
          org={org}
          triggerType={triggerType}
          onClose={toggleDialog}
          interval={interval}
        />
      );
    }

    if (!section) {
      throw new Error('Section needed for applicant automations');
    }

    // if we are adding an org-default, we will use the first job to display the message template
    const parserJob = editingForJob || (jobs || [])[0];

    const parser = new ApplicantMessageTemplateParser({
      useStandInApplicant: true,
      ...(parserJob ? { job: parserJob } : { org, useStandInJob: true }),
      user,
    });

    return (
      <ApplicantAutomationDialog
        key={`creating-automation-${section}`}
        section={section}
        orgId={org.org_id}
        orgName={org.name}
        parser={parser}
        onClose={toggleDialog}
        editingForJob={editingForJob}
        parentAutomationsForSection={parentAutomationsForSection}
        automation={getApplicantAutomationCreateDefaults(triggerType)}
      />
    );
  };

  return (
    <>
      {variant === 'small' ? (
        <IconButton
          size="small"
          onClick={handleAddAutomation}
          aria-haspopup="dialog"
          className={`${className || ''} u-alignSelf-flexStart u-padding-none`}
        >
          <MaterialDesignIcon iconName={ICON_NAMES.ADD_CIRCLE} />
        </IconButton>
      ) : (
        <UiButton
          size={variant === 'medium' ? 'sm' : 'default'}
          variant={variant === 'medium' ? 'dismiss' : 'primary'}
          onClick={handleAddAutomation}
          className={`${className} ${variant === 'medium' ? 'u-color-secondary' : ''}`}
          dataCy="add-automation"
        >
          {variant === 'medium' ? '+ Add' : 'Add Automation'}
        </UiButton>
      )}
      {dialogVisible && renderDialog()}
    </>
  );
};
